import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { PortalHeading } from "@/pages/portal/PortalLayout";
import { useInsurer } from "@/context/InsurerContext";
import { DAMAGE_AREAS, INCIDENT_TYPES, SEVERITIES, harareToday, insApi, insFileUrl } from "@/lib/insurance";
import { errMsg } from "@/lib/api";

const TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic"];
const MAX = 10 * 1024 * 1024;
const today = harareToday();
const EMPTY = {
  policy_number: "", claim_number: "", insured_name: "", insured_phone: "",
  make: "", model: "", year: "", colour: "", registration: "", vin: "",
  incident_type: "accident_damage", incident_other: "", incident_date: "", damage_description: "",
  drivable: "unknown", recovery_required: "no", damage_areas: [], severity: "moderate",
  notes: "", preferred_from: "", preferred_to: "",
};

const Field = ({ label, children, req }) => (
  <label className="block space-y-1.5"><span className="text-xs font-semibold uppercase tracking-wider text-[#56585E]">{label}{req && <span className="text-[#F86900]"> *</span>}</span>{children}</label>
);
const Card = ({ title, children }) => (
  <section className="rounded-2xl bg-white p-5 sm:p-6"><h2 className="font-display font-bold text-lg text-[#0D141A] mb-4">{title}</h2><div className="grid gap-4 sm:grid-cols-2">{children}</div></section>
);
const Choice = ({ options, value, onChange, testId }) => (
  <div className="flex flex-wrap gap-2" data-testid={testId}>
    {options.map(([k, l]) => (
      <button type="button" key={k} onClick={() => onChange(k)} data-testid={`${testId}-${k}`}
        className={`rounded-full border px-4 py-1.5 text-sm transition-colors ${value === k || (Array.isArray(value) && value.includes(k)) ? "bg-[#020165] border-[#020165] text-white" : "bg-white hover:border-[#020165]"}`}>{l}</button>
    ))}
  </div>
);

const Photos = ({ photos, setPhotos }) => {
  const [progress, setProgress] = useState({});
  const add = async (files) => {
    for (const file of Array.from(files)) {
      if (!TYPES.includes(file.type)) { toast.error(`${file.name}: only JPG, PNG, WEBP or HEIC`); continue; }
      if (file.size > MAX) { toast.error(`${file.name}: larger than 10 MB`); continue; }
      const key = `${file.name}-${Date.now()}`;
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await insApi.post("/files/upload", fd, { onUploadProgress: (e) => setProgress((p) => ({ ...p, [key]: Math.round((e.loaded / (e.total || 1)) * 100) })) });
        setPhotos((p) => [...p, r.data.id]);
      } catch (e) { toast.error(errMsg(e, "Upload failed")); }
      setProgress((p) => { const n = { ...p }; delete n[key]; return n; });
    }
  };
  return (
    <div className="sm:col-span-2 space-y-3">
      <label className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[#020165]/25 bg-[#F4F4F4] p-6 text-sm text-[#56585E] hover:border-[#F86900] transition-colors">
        <Upload className="h-6 w-6 text-[#020165]" />Add damage photos (JPG, PNG, WEBP, HEIC · max 10 MB each)
        <input type="file" multiple accept={TYPES.join(",")} className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ""; }} data-testid="insurer-photo-input" />
      </label>
      {Object.entries(progress).map(([k, v]) => (
        <div key={k} className="flex items-center gap-3 text-xs" data-testid="insurer-upload-progress"><Loader2 className="h-4 w-4 animate-spin" /><div className="h-1.5 flex-1 rounded bg-[#F4F4F4]"><div className="h-1.5 rounded bg-[#F86900]" style={{ width: `${v}%` }} /></div>{v}%</div>
      ))}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2" data-testid="insurer-photo-list">
        {photos.map((id) => (
          <div key={id} className="relative">
            <img src={insFileUrl(id)} alt="" className="aspect-square w-full rounded-lg object-cover" />
            <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x !== id))} className="absolute right-1 top-1 rounded-full bg-white/90 p-1" aria-label="Remove photo"><X className="h-3 w-3" /></button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default function InsurerRequestForm() {
  const { insurer } = useInsurer();
  const [f, setF] = useState(EMPTY);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const toggleArea = (k) => setF({ ...f, damage_areas: f.damage_areas.includes(k) ? f.damage_areas.filter((a) => a !== k) : [...f.damage_areas, k] });
  const inp = (k, props = {}) => <Input value={f[k]} onChange={set(k)} data-testid={`insurer-form-${k.replace(/_/g, "-")}`} {...props} />;

  const submit = async (e) => {
    e.preventDefault();
    if (!f.damage_areas.length) return toast.error("Select at least one damage area");
    if (!photos.length) return toast.error("Add at least one photo");
    setBusy(true);
    const opt = (v) => (v ? v : null);
    try {
      const r = await insApi.post("/requests", {
        policy_number: f.policy_number, claim_number: f.claim_number, insured_name: f.insured_name, insured_phone: f.insured_phone,
        vehicle: { make: f.make, model: f.model, year: f.year ? Number(f.year) : null, colour: opt(f.colour), registration: f.registration, vin: opt(f.vin) },
        incident_type: f.incident_type, incident_other: opt(f.incident_other), incident_date: f.incident_date,
        damage_description: f.damage_description, drivable: f.drivable, recovery_required: f.recovery_required,
        damage_areas: f.damage_areas, severity: f.severity, photo_ids: photos, notes: opt(f.notes),
        preferred_from: opt(f.preferred_from), preferred_to: opt(f.preferred_to),
      });
      setDone(r.data.request);
    } catch (err) { toast.error(errMsg(err, "Could not submit request")); } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="mx-auto max-w-lg rounded-2xl bg-white p-8 text-center" data-testid="insurer-request-success">
        <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-600" />
        <h1 className="mt-4 font-display text-2xl font-extrabold">Request submitted</h1>
        <p className="mt-2 text-sm text-[#56585E]">Use this reference to track the request with Accelary Motors.</p>
        <p className="mt-4 font-display text-3xl font-extrabold tracking-wide text-[#020165]" data-testid="insurer-request-success-reference">{done.reference}</p>
        <p className="mt-1 text-xs text-[#56585E]">Your claim reference: {done.claim_number}</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to={`/insurer/requests/${done.id}`} className="rounded-full bg-[#020165] px-5 py-2.5 text-sm font-semibold text-white" data-testid="insurer-request-success-view">View request</Link>
          <button onClick={() => { setDone(null); setF(EMPTY); setPhotos([]); }} className="rounded-full border px-5 py-2.5 text-sm font-semibold" data-testid="insurer-request-success-new">Submit another</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5 max-w-4xl" data-testid="insurer-request-form">
      <PortalHeading title="New quote request" sub="Motor vehicle repair or assessment quote" />
      <Card title="Claim">
        <Field label="Insurer company"><Input value={insurer?.company_name || ""} disabled data-testid="insurer-form-company" /></Field>
        <Field label="Policy number" req>{inp("policy_number", { required: true })}</Field>
        <Field label="Claim reference number" req>{inp("claim_number", { required: true })}</Field>
        <Field label="Insured's full name" req>{inp("insured_name", { required: true })}</Field>
        <Field label="Insured's contact number" req>{inp("insured_phone", { required: true, type: "tel", placeholder: "+263 …", minLength: 5 })}</Field>
      </Card>
      <Card title="Vehicle">
        <Field label="Make" req>{inp("make", { required: true })}</Field>
        <Field label="Model" req>{inp("model", { required: true })}</Field>
        <Field label="Year">{inp("year", { type: "number", min: 1950, max: 2100 })}</Field>
        <Field label="Colour">{inp("colour")}</Field>
        <Field label="Registration number" req>{inp("registration", { required: true })}</Field>
        <Field label="VIN (if available)">{inp("vin")}</Field>
      </Card>
      <Card title="Incident & damage">
        <div className="sm:col-span-2"><Field label="Type of incident" req><Choice options={INCIDENT_TYPES} value={f.incident_type} onChange={set("incident_type")} testId="insurer-form-incident-type" /></Field></div>
        {f.incident_type === "other" && <div className="sm:col-span-2"><Field label="Describe the incident" req>{inp("incident_other", { required: true })}</Field></div>}
        <Field label="Date of incident" req>{inp("incident_date", { required: true, type: "date", max: today })}</Field>
        <div className="sm:col-span-2"><Field label="Brief description of damage" req><textarea required rows={3} value={f.damage_description} onChange={set("damage_description")} className="w-full rounded-md border p-3 text-sm" data-testid="insurer-form-damage-description" /></Field></div>
        <Field label="Drivable" req><Choice options={[["yes", "Yes"], ["no", "No"], ["unknown", "Unknown"]]} value={f.drivable} onChange={set("drivable")} testId="insurer-form-drivable" /></Field>
        <Field label="Recovery required" req><Choice options={[["yes", "Yes"], ["no", "No"]]} value={f.recovery_required} onChange={set("recovery_required")} testId="insurer-form-recovery" /></Field>
        <div className="sm:col-span-2"><Field label="Estimated damage areas" req><Choice options={DAMAGE_AREAS} value={f.damage_areas} onChange={toggleArea} testId="insurer-form-area" /></Field></div>
        <div className="sm:col-span-2"><Field label="Insurer severity assessment" req><Choice options={SEVERITIES} value={f.severity} onChange={set("severity")} testId="insurer-form-severity" /></Field></div>
      </Card>
      <Card title="Photos (at least one)"><Photos photos={photos} setPhotos={setPhotos} /></Card>
      <Card title="Scheduling & notes">
        <Field label="Preferred assessment from">{inp("preferred_from", { type: "date", min: today })}</Field>
        <Field label="Preferred assessment to">{inp("preferred_to", { type: "date", min: f.preferred_from || today })}</Field>
        <div className="sm:col-span-2"><Field label="Additional notes or special instructions"><textarea rows={3} value={f.notes} onChange={set("notes")} className="w-full rounded-md border p-3 text-sm" data-testid="insurer-form-notes" /></Field></div>
      </Card>
      <button disabled={busy} data-testid="insurer-form-submit" className="w-full sm:w-auto rounded-full bg-[#F86900] px-8 py-3 font-bold text-[#0D141A] hover:bg-[#ff7a1a] disabled:opacity-60 transition-colors">
        {busy ? "Submitting…" : "Submit request"}
      </button>
    </form>
  );
}
